import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Login from './pages/Login';
import CreatorPortal from './pages/CreatorPortal';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { useAuth } from './context/AuthContext';
import { getReturnPath } from './helpers/returnPath';

const PrivateApp = lazy(() => import('./PrivateApp.jsx'));

const PUBLIC_PATH_PREFIXES = ['/login', '/privacy-policy', '/creator-portal'];

const isPublicPath = (pathname = '') =>
  PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const LoadingScreen = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
    <CircularProgress />
  </Box>
);

const App = () => {
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    const currentPath = location?.pathname || '/';
    const previousCurrentPath = sessionStorage.getItem('app.currentPathname') || '';
    if (previousCurrentPath !== currentPath) {
      sessionStorage.setItem('app.previousPathname', previousCurrentPath);
    }
    sessionStorage.setItem('app.currentPathname', currentPath);
  }, [location?.pathname]);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getReturnPath(location.state?.from)} replace /> : <Login />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/creator-portal" element={<CreatorPortal />} />
      <Route path="/creator-portal/activations" element={<CreatorPortal />} />
      <Route path="/creator-portal/:uuid" element={<CreatorPortal />} />
      <Route path="/creator-portal/:uuid/activations" element={<CreatorPortal />} />
      <Route
        path="*"
        element={
          user ? (
            <Suspense fallback={<LoadingScreen />}>
              <PrivateApp />
            </Suspense>
          ) : isPublicPath(location?.pathname || '') ? (
            <Navigate to="/login" replace />
          ) : (
            <Navigate to="/login" state={{ from: location }} replace />
          )
        }
      />
    </Routes>
  );
};

export default App;
